// src/utils/liveStationIds.ts
//
// Identita stanoviště živého bodování (docs/live-faze2-navrh.md bod 3.1–3.2):
//
// - Id stanoviště = uložené pole `id`, jinak id dokumentu – přesně jak ho
//   skládají čtenáři (`{ id: d.id, ...d.data() }`: JudgeView, Živé výsledky,
//   tabule, publikace). Pod tímto id jsou výsledky (`stations[id]`),
//   koncepty (`{id}_{startovní číslo}`) i dual rezervace (`{id}_{runda}`).
//   OM Slovenska open: dokument st5 s uloženým id st6 → výsledky pod st6.
// - Id se po vzniku nemění a smazané se znovu nepřidělí: nové stanoviště
//   dostane st{k}, k = 1 + nejvyšší číslo, které se kdy objevilo (počítadlo
//   závodu liveStationSeq, id a id dokumentů stanovišť, klíče výsledků,
//   konceptů a rezervací). Pozdní zápis rozhodčího se starou obrazovkou by
//   jinak „ožil“ u nového stanoviště.
// - Pravidla pustí zápis rozhodčího jen pro st1–st999 bez nuly na začátku.

/** Id stanoviště, které pravidla přijmou v zápisu rozhodčího (lastStationId). */
export const STATION_ID_RE = /^st[1-9][0-9]{0,2}$/;

/** Nejvyšší číslo id stanoviště (st999). */
export const MAX_STATION_ID_NUMBER = 999;

/** Id stanoviště jako u čtenářů: uložené `id` má přednost před id dokumentu. */
export function effectiveStationId(docId: string, data: { id?: unknown } | null | undefined): string {
  const stored = data ? data.id : undefined;
  if (stored === undefined) return docId;
  return typeof stored === "string" ? stored : String(stored);
}

/** Číslo z id tvaru st{číslo} (i st01, st0 – pro výpočet maxima), jinak null. */
export function stationIdNumber(id: unknown): number | null {
  if (typeof id !== "string") return null;
  const m = /^st(\d{1,6})$/.exec(id);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Id stanoviště z klíče konceptu (`{id}_{startovní číslo}`) nebo dual
 * rezervace (`{id}_{runda}`, `{id}_{runda}_notif`): část před prvním `_`.
 */
export function stationIdFromAuxKey(key: string): string {
  const i = key.indexOf("_");
  return i < 0 ? key : key.slice(0, i);
}

/** Výsledek střelce – jen pole, ze kterých se čtou id stanovišť. */
export interface ResultStationKeysLike {
  stations?: unknown;
  lockedStations?: unknown;
  lastStationId?: unknown;
}

/** Všechna id stanovišť, která výsledek zmiňuje (klíče stations, lockedStations, lastStationId). */
export function stationIdsInResult(result: ResultStationKeysLike | null | undefined): string[] {
  if (!result) return [];
  const out: string[] = [];
  if (result.stations && typeof result.stations === "object") out.push(...Object.keys(result.stations as object));
  if (Array.isArray(result.lockedStations)) {
    result.lockedStations.forEach((s) => {
      if (typeof s === "string") out.push(s);
    });
  }
  if (typeof result.lastStationId === "string" && result.lastStationId) out.push(result.lastStationId);
  return out;
}

/**
 * Číslo id pro nové stanoviště: 1 + maximum z počítadla závodu
 * (liveStationSeq) a čísel všech zadaných id. Nad st999 null (pravidla by
 * zápisy rozhodčích zamítla).
 */
export function nextStationIdNumber(seq: unknown, ids: Iterable<string>): number | null {
  let max = typeof seq === "number" && Number.isFinite(seq) && seq > 0 ? Math.floor(seq) : 0;
  for (const id of Array.from(ids)) {
    const n = stationIdNumber(id);
    if (n !== null && n > max) max = n;
  }
  const next = max + 1;
  return next <= MAX_STATION_ID_NUMBER ? next : null;
}
